import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Col, Container, Row } from 'react-bootstrap'

import { CONTACT_UPDATE_SUPPORTED_INTEGRATIONS, IntegrationServiceId, TASK_EXPORT_SUPPORTED_INTEGRATIONS } from '../../../common/integrations'
import { IntegrationEmailThread } from '../../../models/Email'
import { selectEmailThreads, selectEmailThreadsLoading } from '../../../store/emailHub/emailThreads'
import { selectContactsForThread, selectEmailCurrentUser } from '../../../store/emailHub/emailUsers'
import { selectTasks } from '../../../store/task/task'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { AddTaskToCRMModal } from '../../MeetingSummary/CRM/V2/AddTaskToCRMModal'
import { TaskSourceType } from '../../Tasks/hooks'

import { EmailResponseView } from '../EmailResponse/EmailResponseView'
import { useLastContactEmailFromThread } from '../Table/hooks'
import { useFetchEmailThreadTasks } from '../hooks'

import { EmailView } from './EmailView'
import { UpdateExternalEmailContactModal } from '../EmailResponse/UpdateExternalEmailContact'
import { selectShouldReload, setShouldReload, setUpdateStartedFrom } from '../../../store/meetingSummary/contactUpdate'

export interface EmailThreadViewProps {
  emailConnector: string
  threadId: string
}

export const EmailThreadView: React.FC<EmailThreadViewProps> = ({ emailConnector, threadId }) => {
  const emailThreads = useAppSelector(selectEmailThreads)
  const emailThreadsLoading = useAppSelector(selectEmailThreadsLoading)

  if (emailThreadsLoading) {
    return <div className='skeleton mt-2' style={{ width: '100%', height: '300px' }} />
  }

  const emailThread = emailThreads.find((thread) => thread.externalId === threadId)

  if (!emailThread) {
    return <div>No email thread found</div>
  }

  return <EmailThreadBody emailConnector={emailConnector} thread={emailThread} />
}

interface EmailThreadBodyProps {
  emailConnector: string
  thread: IntegrationEmailThread
}

const EmailThreadBody: React.FC<EmailThreadBodyProps> = ({ emailConnector, thread }) => {
  const { externalId: threadId } = thread
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectEmailCurrentUser)
  const contactsForThread = useAppSelector(selectContactsForThread(threadId))
  const tasks = useAppSelector(selectTasks)
  const shouldReload = useAppSelector(selectShouldReload)
  const [showUpdateParticipantModal, setShowUpdateParticipantModal] = useState<boolean>(false)

  const navigate = useNavigate()
  const { modal = null, serviceId: modalServiceId = null, contactId: modalContactId = null } = useParams()

  const { lastEmailByContact } = useLastContactEmailFromThread(thread)
  const { fetchTasks } = useFetchEmailThreadTasks()

  const taskExportSupportedUserIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => TASK_EXPORT_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? [],
    [currentUser],
  )

  const selectedTaskExportIntegration = useMemo(
    () => taskExportSupportedUserIntegrations.find((e) => e.serviceId === modalServiceId) ?? null,
    [modalServiceId, taskExportSupportedUserIntegrations],
  )

  const contactUpdateSupportedUserIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? [],
    [currentUser],
  )

  const selectedContactUpdateIntegration = useMemo(
    () => contactUpdateSupportedUserIntegrations.find((e) => e.serviceId === modalServiceId) ?? null,
    [modalServiceId, contactUpdateSupportedUserIntegrations],
  )

  const selectedContact = useMemo(() => contactsForThread.find((e) => e.id === modalContactId) ?? null, [contactsForThread, modalContactId])

  const handleHideModal = async (isSaved?: boolean) => {
    navigate('.')
    if (isSaved) {
      await fetchTasks(emailConnector, threadId)
    }
    setShowUpdateParticipantModal(false)
    dispatch(setUpdateStartedFrom(null))
  }

  useEffect(() => {
    if (!showUpdateParticipantModal && shouldReload) {
      dispatch(setShouldReload(false))
      navigate(0)
    }
  }, [showUpdateParticipantModal, shouldReload, dispatch, navigate])

  useEffect(() => {
    if (modal === 'contact-update' && selectedContact && selectedContactUpdateIntegration) {
      setShowUpdateParticipantModal(true)
      dispatch(setUpdateStartedFrom('email'))
    }
  }, [modal, selectedContact, selectedContactUpdateIntegration, dispatch])

  return (
    <>
      {modal === 'contact-update' && selectedContact && selectedContactUpdateIntegration ? (
        <UpdateExternalEmailContactModal
          user={selectedContact}
          serviceId={selectedContactUpdateIntegration.serviceId}
          emailConnector={emailConnector}
          threadId={threadId}
          show={true}
          onCancel={handleHideModal}
          onSave={handleHideModal}
        />
      ) : null}
      {modal === 'export-tasks' && selectedTaskExportIntegration && lastEmailByContact ? (
        <AddTaskToCRMModal
          show={true}
          onHide={handleHideModal}
          userIntegration={selectedTaskExportIntegration}
          tasks={tasks}
          taskContextDetails={{ threadId, emailConnector, emailId: lastEmailByContact.externalId }}
          type={TaskSourceType.EMAIL}
        />
      ) : null}
      {/*{modal === 'client-profile' && selectedContact ? (*/}
      {/*  <EmailResponseClientProfileModal show={true} onHide={handleHideModal} emailConnector={emailConnector} threadId={threadId} contact={selectedContact} />*/}
      {/*) : null}*/}
      <Container fluid>
        <Row>
          <Col>
            <div className='d-flex flex-column'>
              {thread.emails.map((email, idx) => {
                return <EmailView key={email.externalId} email={email} initialCollapsed={idx !== thread.emails.length - 1} />
              })}
              <EmailResponseView thread={thread} />
            </div>
          </Col>
        </Row>
      </Container>
    </>
  )
}
